<script setup lang="ts">

import {Dialog, DialogContent, DialogTrigger} from "@/core/components/ui/dialog";
import {SurveyAnswerInstance} from "@/core/interfaces/survey/Survey";
import {computed, ref, watch} from "vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import InterviewSurveyBuilt from "@/interview/components/instance-modal/InterviewSurveyBuilt.vue";
import {CircleAlert} from "@lucide/vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {surveyAnswerInstanceApi} from "@/container";
import {useInterviewStore} from "@/stores/interview-store";
import dayjs from "dayjs";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/core/components/ui/tooltip";
import useSurveyForm from "@/survey/composables/useSurveyForm";

const interviewStore = useInterviewStore();
const {instance, submitting} = defineProps<{ instance?: SurveyAnswerInstance; submitting: boolean }>();
const emits = defineEmits<{
    (e: 'save', answers: Record<number, (string | number | boolean | undefined)[]>): void;
}>();

const survey = computed(() => {
    return instance?.survey;
});

const {
    initAnswers,
    handleChange,
    submit,
    userAnswers,
    errors
} = useSurveyForm(survey);

const loadingSurvey = ref(false);
const downloadingSurveyInstance = ref(false);

watch(() => instance, (newInstance) => {
    if (newInstance?.survey) {
        initAnswers(newInstance.survey);
    }
}, {immediate: true});

const handleSubmit = async () => {
    submit(
        (answers) => emits('save', answers)
    );
};

/**
 * Compute the audio file name.
 */
const fileName = computed(() => {
    const name = interviewStore.consumerName?.replace(/\s/g, '_') ?? '';
    const date = dayjs().format("DD_MM_YYYY");

    return `${interviewStore.interview?.name}_${name}_${date}`;
});

/**
 * Download the survey instance as PDF.
 */
const downloadSurveyInstance = async (): Promise<void> => {
    if (!instance?.id) {
        return Promise.resolve();
    }

    downloadingSurveyInstance.value = true;
    return surveyAnswerInstanceApi()
        .export(instance.id)
        .then(data => {
            const objectUrl = URL.createObjectURL(data);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = fileName.value + ".pdf";
            a.click();

            URL.revokeObjectURL(objectUrl);
        })
        .finally(() => {
            downloadingSurveyInstance.value = false;
        });
};
</script>

<template>
  <Dialog>
    <DialogTrigger>
      <slot />
    </DialogTrigger>
    <DialogContent class="lg:max-w-4xl max-h-[90vh] flex flex-col gap-2">
      <BaseFlexSpinner v-if="loadingSurvey" />
      <template v-else-if="instance">
        <div v-if="!instance.completed_at" class="flex-shrink-0">
          <span class="text-red-500 flex items-center gap-2 font-medium leading-none">
            <CircleAlert class="w-5 h-5" />
            Vérifiez et complétez le compte rendu
          </span>
        </div>
        <div class="overflow-y-auto flex-1 pr-4">
          <InterviewSurveyBuilt
            density="compact"
            :survey="instance.survey"
            :answers="userAnswers"
            :display-collapsible="false"
            :errors="errors"
            compact
            @change="handleChange"
          />
        </div>
        <div class="flex justify-end gap-2 flex-shrink-0">
          <BaseButton :primary="!instance.completed_at" :loading="submitting" @click="handleSubmit">
            Enregistrer
          </BaseButton>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger as-child>
                <div>
                  <BaseButton
                    :primary="!!instance.completed_at"
                    :disabled="!instance.completed_at"
                    :loading="downloadingSurveyInstance"
                    @click="downloadSurveyInstance"
                  >
                    Télécharger
                  </BaseButton>
                </div>
              </TooltipTrigger>
              <TooltipContent v-if="!instance.completed_at" class="bg-primary font-medium text-base">
                Veuillez enregistrer le compte-rendu avant de le télécharger
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </template>
    </DialogContent>
  </Dialog>
</template>

<style scoped>

</style>
